<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- 当前运营控制台源码维护说明，更新时间：2026-09-06 10:05 CST，平台 v0.1.86。恢复真实 IP 集合接口，保存、刷新列表和删除形成完整闭环。 -->
<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { onMounted, ref } from 'vue';
import { consoleApi } from '../api/client';
import type { IpCollection } from '../api/types';

const collections = ref<IpCollection[]>([]);
const error = ref('');
const success = ref('');
const saving = ref(false);
const deletingId = ref<number>();
// 表单漏填属于字段级提示，不占用页面顶部的全局错误区域。
const fieldErrors = ref({ collectionName: '', cidrs: '' });
// 草稿与已确认 IP 分离：粘贴后仍可修改，按回车或点击按钮才生成标签。
const cidrDraft = ref('');
const cidrs = ref<string[]>([]);
const form = ref({
  collectionName: '',
  description: ''
});

onMounted(loadCollections);

async function loadCollections() {
  try {
    error.value = '';
    collections.value = await consoleApi.security.ipCollections();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'IP 集合加载失败';
  }
}

async function createCollection() {
  if (saving.value || deletingId.value !== undefined) return;
  // 保存集合时也接纳尚未按回车的草稿，避免用户最后一条 IP 被遗漏。
  const pendingCidrs = parseCidrs(cidrDraft.value);
  const collectionCidrs = [...new Set([...cidrs.value, ...pendingCidrs])];
  fieldErrors.value = {
    collectionName: form.value.collectionName.trim() ? '' : '请输入集合名称',
    cidrs: collectionCidrs.length ? '' : '请输入 IP 或 CIDR'
  };
  // 一次标出全部漏填字段，让用户不用反复提交才能发现下一项。
  if (fieldErrors.value.collectionName || fieldErrors.value.cidrs) return;
  saving.value = true;
  try {
    error.value = '';
    success.value = '';
    const description = form.value.description.trim() || undefined;
    await consoleApi.security.createIpCollection({
      collectionName: form.value.collectionName.trim(),
      collectionType: 'IP_CIDR',
      description,
      items: collectionCidrs.map(cidr => ({
        cidr,
        description
      }))
    });
    form.value.collectionName = '';
    form.value.description = '';
    cidrDraft.value = '';
    cidrs.value = [];
    success.value = `IP 集合已保存，共 ${collectionCidrs.length} 条 IP/CIDR。请到“策略管理”选择集合并绑定到域名。`;
    await loadCollections();
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'IP 集合保存失败';
    ElMessage.error(error.value);
  } finally { saving.value = false; }
}

function parseCidrs(value: string) {
  return value.split(/[\s,，]+/).map(item => item.trim()).filter(Boolean);
}

function addCidrs() {
  const additions = parseCidrs(cidrDraft.value);
  if (!additions.length) return;
  cidrs.value = [...new Set([...cidrs.value, ...additions])];
  cidrDraft.value = '';
  fieldErrors.value.cidrs = '';
}

function removeCidr(cidr: string) {
  cidrs.value = cidrs.value.filter(item => item !== cidr);
}

async function deleteCollection(row: IpCollection) {
  if (saving.value || deletingId.value !== undefined) return;
  deletingId.value = row.id;
  try {
    error.value = '';
    success.value = '';
    await consoleApi.security.deleteIpCollection(row.id);
    success.value = 'IP 集合已删除';
    await loadCollections();
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'IP 集合删除失败';
    ElMessage.error(error.value);
  } finally { deletingId.value = undefined; }
}

function itemSummary(row: IpCollection) {
  return row.items?.map(item => item.cidr).join('、') || '暂无内容';
}
</script>

<template>
  <section class="page">
    <div class="page-title page-title-row">
      <div>
        <h1>IP 集合</h1>
        <p>创建可复用的 IP/CIDR 集合。集合创建不需要选择域名，后续应在策略里选择集合并绑定生效范围。</p>
      </div>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon />
    <el-alert v-if="success" :title="success" type="success" show-icon />

    <el-card class="flow-card">
      <template #header>IP 集合生效流程</template>
      <ol class="flow-steps">
        <li>
          <strong>创建集合</strong>
          <span>集合创建不绑定域名，只维护可复用的 IP/CIDR 名单。</span>
        </li>
        <li>
          <strong>策略引用</strong>
          <span>到“策略管理”选择集合，让它对指定加速域名放行或拦截。</span>
        </li>
        <li>
          <strong>保存即发布</strong>
          <span>保存策略后自动生成当前域名配置包，不再进入独立发布页。</span>
        </li>
        <li>
          <strong>同步到 Edge</strong>
          <span>当前域名自动同步成功后，Edge 会按集合规则真正生效。</span>
        </li>
      </ol>
    </el-card>

    <el-card class="form-card">
      <template #header>创建 IP 集合</template>
      <el-form label-position="top">
        <el-form-item label="集合名称" required :error="fieldErrors.collectionName">
          <el-input v-model="form.collectionName" placeholder="例如：办公网络、合作伙伴、临时封禁名单" @input="fieldErrors.collectionName = ''" />
        </el-form-item>
        <el-form-item label="IPv4 / IPv6 或 CIDR" required :error="fieldErrors.cidrs">
          <div class="cidr-entry-row">
            <div class="cidr-editor" @click="($refs.cidrInput as HTMLInputElement)?.focus()">
              <button
                v-for="cidr in cidrs"
                :key="cidr"
                type="button"
                class="cidr-chip"
                :aria-label="`删除 ${cidr}`"
                @click.stop="removeCidr(cidr)"
              >
                {{ cidr }} <span aria-hidden="true">×</span>
              </button>
              <textarea
                ref="cidrInput"
                v-model="cidrDraft"
                class="cidr-draft"
                placeholder="支持 IPv4、IPv6 和 CIDR，例如 203.0.113.8、2001:db8::1、2001:db8::/64"
                rows="1"
                @keydown.enter.exact.prevent="addCidrs"
                @blur="addCidrs"
              ></textarea>
            </div>
            <el-button type="success" plain :disabled="!cidrDraft.trim()" @click="addCidrs">添加 IP</el-button>
          </div>
          <small class="cidr-count">已添加 {{ cidrs.length }} 条；支持 IPv4、IPv6 和 CIDR，可粘贴多条。</small>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.description" placeholder="可选，例如办公室出口 IP" />
        </el-form-item>
        <p class="status-help">集合创建不需要选择域名；保存后请到“策略管理”选择集合，让它对指定加速域名生效。</p>
        <el-button type="primary" :loading="saving" :disabled="saving || deletingId !== undefined" @click="createCollection">{{ saving ? '保存中…' : '保存集合' }}</el-button>
      </el-form>
    </el-card>

    <h2>可复用集合</h2>
    <el-table :data="collections">
      <el-table-column prop="collectionName" label="集合名称" />
      <el-table-column prop="itemCount" label="IP/CIDR 数量" />
      <el-table-column label="集合内容">
        <template #default="{ row }">{{ itemSummary(row) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="状态" />
      <el-table-column prop="description" label="备注" />
      <el-table-column label="操作" width="160">
        <template #default="{ row }">
          <el-button size="small" type="danger" :loading="deletingId === row.id" :disabled="saving || deletingId !== undefined" @click="deleteCollection(row)">{{ deletingId === row.id ? '删除中…' : '删除' }}</el-button>
        </template>
      </el-table-column>
    </el-table>
  </section>
</template>

<style scoped>
.cidr-entry-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
  width: 100%;
}

.cidr-editor {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px;
  width: 100%;
  min-height: 86px;
  padding: 11px 12px;
  overflow: visible;
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-border-radius-base);
  background: var(--el-fill-color-blank);
  cursor: text;
  transition: border-color .2s, box-shadow .2s;
}

.cidr-editor:focus-within {
  border-color: var(--el-color-primary);
  box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

.cidr-chip {
  height: 28px;
  padding: 0 9px;
  border: 1px solid #acd9b6;
  border-radius: 5px;
  color: #17683d;
  font: inherit;
  font-weight: 650;
  background: #edf9ef;
  cursor: pointer;
}

.cidr-chip span {
  margin-left: 4px;
  color: #42a85d;
}

.cidr-draft {
  flex: 1 1 280px;
  min-width: 180px;
  min-height: 28px;
  padding: 0;
  border: 0;
  outline: 0;
  resize: none;
  overflow: hidden;
  color: var(--el-text-color-primary);
  font: inherit;
  line-height: 28px;
  background: transparent;
}

.cidr-draft::placeholder,
.cidr-count {
  color: var(--el-text-color-placeholder);
}

.cidr-count {
  display: block;
  margin-top: 8px;
}

@media (max-width: 720px) {
  .cidr-entry-row {
    grid-template-columns: 1fr;
  }
}
</style>
